import { Template } from '@amcharts/amcharts5';

// Drawing history belongs to a chart instance, so changing symbols cannot replay
// annotations into another chart. Keep Color instances, but copy mutable row data.
const copyDrawingData = (value, seen = new WeakMap()) => {
  if (!value || typeof value !== 'object') return value;
  if (value instanceof Template) {
    if (seen.has(value)) return seen.get(value);
    const template = Template.new({});
    seen.set(value, template);
    template.setAll(copyDrawingData(value._settings, seen));
    return template;
  }
  if (!Array.isArray(value) && Object.getPrototypeOf(value) !== Object.prototype) return value;
  if (seen.has(value)) return seen.get(value);
  const result = Array.isArray(value) ? [] : {};
  seen.set(value, result);
  Object.entries(value).forEach(([key, entry]) => {
    // Runtime sprites are recreated by amCharts, not part of drawing history.
    if (!['sprite', 'label', 'background', '__parse'].includes(key)) {
      result[key] = copyDrawingData(entry, seen);
    }
  });
  return result;
};

const sameDrawingData = (a, b, seen = new WeakMap()) => {
  if (Object.is(a, b)) return true;
  if (a instanceof Template && b instanceof Template) return sameDrawingData(a._settings, b._settings, seen);
  if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
  if (Object.getPrototypeOf(a) !== Object.getPrototypeOf(b)) return false;
  if (!Array.isArray(a) && Object.getPrototypeOf(a) !== Object.prototype) return false;
  if (seen.get(a) === b) return true;
  seen.set(a, b);
  const keys = Object.keys(a);
  return keys.length === Object.keys(b).length
    && keys.every(key => Object.prototype.hasOwnProperty.call(b, key) && sameDrawingData(a[key], b[key], seen));
};

export function createDrawingHistory({ stockChart, onComplete, onChange, onRestore = () => {} }) {
  const snapshot = () => {
    const entries = [];
    stockChart.panels.each(panel => panel.drawings.each(series => {
      if (series.data.length) entries.push({ series, data: copyDrawingData(series.data.values) });
    }));
    return entries;
  };
  let history = [snapshot()];
  let index = 0;
  let restoring = false;
  let disposed = false;
  let revision = 0;
  let pending = false;
  let pendingComplete = false;
  let pointerHeld = false;
  const dom = stockChart.root?.dom;
  const win = dom?.ownerDocument?.defaultView;
  const pointerDown = () => { pointerHeld = true; };
  const pointerUp = () => { if (!pointerHeld) return; pointerHeld = false; capture(false); };
  dom?.addEventListener('pointerdown', pointerDown, true);
  win?.addEventListener('pointerup', pointerUp);
  win?.addEventListener('pointercancel', pointerUp);
  const sync = () => onChange({ canUndo: index > 0, canRedo: index < history.length - 1 });
  const drawingInProgress = () => stockChart.panels.values.some(panel =>
    panel.drawings.values.some(series => series._isDrawing));
  const capture = completed => {
    if (restoring || disposed) return;
    pendingComplete ||= completed;
    if (pointerHeld || drawingInProgress()) return;
    if (pending) return;
    pending = true;
    const currentRevision = revision;
    // Wait for the drawing's final point to finish processing before deactivating.
    stockChart.root.events.once('frameended', () => {
      if (disposed || currentRevision !== revision) return;
      pending = false;
      if (pointerHeld || drawingInProgress()) return;
      const shouldComplete = pendingComplete;
      pendingComplete = false;
      const next = snapshot();
      // Redraw/autosave notifications are not edits. In particular, a redraw
      // after undo must not create a duplicate entry and discard redo history.
      if (sameDrawingData(next, history[index])) {
        if (shouldComplete) onComplete();
        return;
      }
      history = history.slice(0, index + 1);
      history.push(next);
      if (history.length > 51) history.shift();
      index = history.length - 1;
      if (shouldComplete) onComplete();
      sync();
    });
  };
  const restore = next => {
    if (disposed || next < 0 || next >= history.length) return;
    revision += 1;
    pending = false;
    pendingComplete = false;
    restoring = true;
    onComplete();
    stockChart.panels.each(panel => panel.drawings.each(series => {
      series.unselectAllDrawings();
      series.cancelDrawing();
      series.clearDrawings();
    }));
    history[next].forEach(({ series, data }) => {
      if (!series.isDisposed()) series.data.setAll(copyDrawingData(data));
    });
    const action = next < index ? 'undo' : 'redo';
    index = next;
    // Derived drawing values are recalculated while data items are rebuilt.
    // Keep these notifications out of history and retain the redo branch.
    const restoredRevision = revision;
    stockChart.root.events.once('frameended', () => {
      if (disposed || revision !== restoredRevision) return;
      history[index] = snapshot();
      restoring = false;
    });
    onRestore(action);
    sync();
  };
  const listeners = [
    stockChart.events.on('drawingadded', event => {
      // Text tools own an editor lifecycle. Never auto-unselect them or patch
      // their frame/style handlers; users leave editing through native controls.
      const isText = event.series?.isType?.('LabelSeries')
        || event.series?.isType?.('CalloutSeries');
      capture(!isText);
    }),
    // Save edited text without touching selection or editor focus.
    stockChart.events.on('drawingsupdated', () => {
      const drawingInProgress = stockChart.panels.values.some(panel =>
        panel.drawings.values.some(series => series._isDrawing));
      if (!drawingInProgress) capture(false);
    }),
    stockChart.events.on('drawingremoved', () => capture(false)),
  ];
  sync();
  return {
    undo: () => restore(index - 1),
    redo: () => restore(index + 1),
    reset: () => {
      revision += 1;
      pending = false;
      pendingComplete = false;
      restoring = true;
      stockChart.panels.each(panel => panel.drawings.each(series => {
        series.cancelDrawing();
        series.unselectAllDrawings();
        series.clearDrawings();
      }));
      history = [snapshot()];
      restoring = false;
      index = 0;
      sync();
    },
    dispose: () => {
      disposed = true;
      dom?.removeEventListener('pointerdown', pointerDown, true);
      win?.removeEventListener('pointerup', pointerUp);
      win?.removeEventListener('pointercancel', pointerUp);
      listeners.forEach(listener => listener.dispose());
      history = [];
    },
  };
}

